/**
 * providers/mockData.ts — deterministic placeholder generators.
 *
 * The mock providers emit realistic-but-fake NORMALIZED records so the engine
 * (and every downstream engine) works fully offline with zero real integrations.
 * Everything is derived from a seeded PRNG keyed on location+domain+index — no
 * city or venue is special-cased.
 */

import type { RoamerLocation } from '@/services/RoamerLocationEngine';
import type {
  FusedCommunity,
  FusedCoworking,
  FusedCurrency,
  FusedEvent,
  FusedHousing,
  FusedMetric,
  FusedPlace,
  FusedRestaurant,
  FusedSafety,
  FusedTransportation,
  FusedWeather,
  FusionDomain,
  GeoPoint,
  WeatherCondition,
} from '../models';

// ─── Seeded PRNG (xmur3 → mulberry32) ──────────────────────────────────────────

function xmur3(str: string): () => number {
  let h = 1779033703 ^ str.length;
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  return () => {
    h = Math.imul(h ^ (h >>> 16), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    return (h ^= h >>> 16) >>> 0;
  };
}

function mulberry32(a: number): () => number {
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function seededRng(seed: string): () => number {
  return mulberry32(xmur3(seed)());
}

export const between = (rng: () => number, min: number, max: number) => min + rng() * (max - min);
export const intBetween = (rng: () => number, min: number, max: number) => Math.floor(between(rng, min, max + 1));
export const pick = <T>(rng: () => number, arr: readonly T[]): T => arr[Math.floor(rng() * arr.length)];
export const chance = (rng: () => number, p: number) => rng() < p;

export function localDayIndex(location: RoamerLocation, now: number): number {
  const offsetMs = (location.longitude / 15) * 3600_000;
  return Math.floor((now + offsetMs) / 86_400_000);
}

/** Jitter a lat/lng around the base so mock POIs are spatially distinct. */
function nearbyPoint(rng: () => number, location: RoamerLocation): GeoPoint {
  return {
    latitude: location.latitude + between(rng, -0.05, 0.05),
    longitude: location.longitude + between(rng, -0.05, 0.05),
  };
}

// Neutral, generic name pools — never city-specific.
const PLACE_PREFIX = ['Central', 'Riverside', 'Old Town', 'Garden', 'Harbor', 'Summit', 'Corner', 'North', 'Union', 'Park'];
const PLACE_SUFFIX: Record<string, string[]> = {
  restaurant: ['Kitchen', 'Bistro', 'Table', 'Eatery', 'Grill', 'House'],
  cafe: ['Coffee', 'Roasters', 'Café', 'Espresso Bar', 'Beans'],
  coworking: ['Workspace', 'Hub', 'Studios', 'Commons', 'Offices'],
  default: ['Center', 'Point', 'Place', 'Spot', 'Station'],
};

function placeName(rng: () => number, domain: FusionDomain): string {
  const suffixes = PLACE_SUFFIX[domain] ?? PLACE_SUFFIX.default;
  return `${pick(rng, PLACE_PREFIX)} ${pick(rng, suffixes)}`;
}

const EMPTY_META = { confidence: 0, lastUpdated: 0, providerSource: '', verificationStatus: 'unverified' as const };

// ─── Generators (meta is stamped later by the engine's quality layer) ──────────

export function genPlaces(location: RoamerLocation, domain: FusionDomain, limit: number, providerId: string): FusedPlace[] {
  const out: FusedPlace[] = [];
  for (let i = 0; i < limit; i++) {
    const rng = seededRng(`${location.locationId}:${domain}:${i}:${providerId}`);
    out.push({
      id: `${providerId}:${domain}:${i}`,
      name: placeName(rng, domain),
      category: domain,
      address: `${intBetween(rng, 1, 200)} ${pick(rng, PLACE_PREFIX)} St`,
      location: nearbyPoint(rng, location),
      rating: Math.round(between(rng, 3.4, 5) * 10) / 10,
      reviewCount: intBetween(rng, 12, 900),
      priceLevel: (intBetween(rng, 1, 4)) as 1 | 2 | 3 | 4,
      distanceKm: Math.round(between(rng, 0.2, 9) * 10) / 10,
      open24h: chance(rng, 0.15),
      tags: [],
      meta: EMPTY_META,
    });
  }
  return out;
}

export function genRestaurants(location: RoamerLocation, limit: number, providerId: string): FusedRestaurant[] {
  const cuisines = ['Local', 'Italian', 'Thai', 'Japanese', 'Vegetarian', 'Fusion', 'Street Food'];
  return genPlaces(location, 'restaurant', limit, providerId).map((p, i) => {
    const rng = seededRng(`${location.locationId}:restaurant-x:${i}:${providerId}`);
    return { ...p, cuisine: pick(rng, cuisines), vegetarianFriendly: chance(rng, 0.5) };
  });
}

export function genCoworking(location: RoamerLocation, limit: number, providerId: string): FusedCoworking[] {
  return genPlaces(location, 'coworking', limit, providerId).map((p, i) => {
    const rng = seededRng(`${location.locationId}:coworking-x:${i}:${providerId}`);
    return {
      ...p,
      dayPassUsd: intBetween(rng, 8, 35),
      monthlyPassUsd: intBetween(rng, 90, 350),
      internetMbps: intBetween(rng, 40, 500),
      has24hAccess: chance(rng, 0.4),
    };
  });
}

export function genHousing(location: RoamerLocation, domain: FusionDomain, limit: number, providerId: string): FusedHousing[] {
  const kindByDomain: Record<string, FusedHousing['kind']> = {
    housing: 'apartment', hotel: 'hotel', hostel: 'hostel', monthlyRental: 'monthlyRental',
  };
  const out: FusedHousing[] = [];
  for (let i = 0; i < limit; i++) {
    const rng = seededRng(`${location.locationId}:${domain}:h:${i}:${providerId}`);
    const kind = kindByDomain[domain] ?? 'other';
    const nightly = kind === 'hotel' || kind === 'hostel';
    out.push({
      id: `${providerId}:${domain}:${i}`,
      title: `${pick(rng, PLACE_PREFIX)} ${nightly ? 'Stay' : 'Residence'} ${intBetween(rng, 1, 40)}`,
      kind,
      location: nearbyPoint(rng, location),
      monthlyCostUsd: nightly ? undefined : intBetween(rng, 350, 3200),
      nightlyCostUsd: nightly ? intBetween(rng, 15, 180) : undefined,
      bedrooms: intBetween(rng, 0, 3),
      furnished: chance(rng, 0.8),
      internetMbps: intBetween(rng, 30, 400),
      minStayDays: nightly ? 1 : intBetween(rng, 7, 90),
      rating: Math.round(between(rng, 3.5, 5) * 10) / 10,
      tags: [],
      meta: EMPTY_META,
    });
  }
  return out;
}

export function genWeather(location: RoamerLocation, now: number, providerId: string): FusedWeather {
  const rng = seededRng(`${location.locationId}:weather:${localDayIndex(location, now)}:${providerId}`);
  const tropical = Math.abs(location.latitude) < 25;
  const base = tropical ? between(rng, 26, 34) : between(rng, 8, 26);
  const willRain = chance(rng, tropical ? 0.45 : 0.25);
  const condition: WeatherCondition = willRain
    ? (chance(rng, 0.25) ? 'storm' : 'rain')
    : pick(rng, ['clear', 'partly-cloudy', 'cloudy'] as const);
  return {
    id: `${providerId}:weather`,
    location: { latitude: location.latitude, longitude: location.longitude },
    tempC: Math.round(base),
    feelsLikeC: Math.round(base + between(rng, -1, 3)),
    highC: Math.round(base + between(rng, 1, 4)),
    lowC: Math.round(base - between(rng, 3, 7)),
    humidityPct: intBetween(rng, tropical ? 55 : 35, tropical ? 90 : 70),
    condition,
    rainStartHour: willRain ? intBetween(rng, 12, 20) : null,
    airQualityIndex: intBetween(rng, 15, 140),
    // Deterministic placeholder sun times (tropics rise/set earlier & flatter).
    sunriseIso: `T0${tropical ? 5 : 6}:${String(intBetween(rng, 10, 59)).padStart(2, '0')}`,
    sunsetIso: `T1${tropical ? 8 : 9}:${String(intBetween(rng, 10, 59)).padStart(2, '0')}`,
    meta: EMPTY_META,
  };
}

/**
 * ISO 3166-1 alpha-2 country code → ISO 4217 currency code.
 *
 * Covers every sovereign state + the most common territories. Used by
 * genCurrency() so the currency card shows "1 USD = 1.00 USD" (USA),
 * "1 USD = 37.45 THB" (Thailand), etc. — never "US$" or "LOCAL".
 */
const COUNTRY_CURRENCY: Record<string, string> = {
  AD: 'EUR', AE: 'AED', AF: 'AFN', AG: 'XCD', AL: 'ALL', AM: 'AMD', AO: 'AOA', AR: 'ARS',
  AT: 'EUR', AU: 'AUD', AZ: 'AZN', BA: 'BAM', BB: 'BBD', BD: 'BDT', BE: 'EUR', BF: 'XOF',
  BG: 'BGN', BH: 'BHD', BI: 'BIF', BJ: 'XOF', BN: 'BND', BO: 'BOB', BR: 'BRL', BS: 'BSD',
  BT: 'BTN', BW: 'BWP', BY: 'BYN', BZ: 'BZD', CA: 'CAD', CD: 'CDF', CF: 'XAF', CG: 'XAF',
  CH: 'CHF', CI: 'XOF', CL: 'CLP', CM: 'XAF', CN: 'CNY', CO: 'COP', CR: 'CRC', CU: 'CUP',
  CV: 'CVE', CY: 'EUR', CZ: 'CZK', DE: 'EUR', DJ: 'DJF', DK: 'DKK', DO: 'DOP', DZ: 'DZD',
  EC: 'USD', EE: 'EUR', EG: 'EGP', ER: 'ERN', ES: 'EUR', ET: 'ETB', FI: 'EUR', FJ: 'FJD',
  FR: 'EUR', GA: 'XAF', GB: 'GBP', GD: 'XCD', GE: 'GEL', GH: 'GHS', GM: 'GMD', GN: 'GNF',
  GQ: 'XAF', GR: 'EUR', GT: 'GTQ', GW: 'XOF', GY: 'GYD', HN: 'HNL', HR: 'EUR', HT: 'HTG',
  HU: 'HUF', ID: 'IDR', IE: 'EUR', IL: 'ILS', IN: 'INR', IQ: 'IQD', IR: 'IRR', IS: 'ISK',
  IT: 'EUR', JM: 'JMD', JO: 'JOD', JP: 'JPY', KE: 'KES', KG: 'KGS', KH: 'KHR', KI: 'AUD',
  KM: 'KMF', KN: 'XCD', KP: 'KPW', KR: 'KRW', KW: 'KWD', KZ: 'KZT', LA: 'LAK', LB: 'LBP',
  LC: 'XCD', LI: 'CHF', LK: 'LKR', LR: 'LRD', LS: 'LSL', LT: 'EUR', LU: 'EUR', LV: 'EUR',
  LY: 'LYD', MA: 'MAD', MD: 'MDL', ME: 'EUR', MG: 'MGA', MK: 'MKD', ML: 'XOF', MM: 'MMK',
  MN: 'MNT', MO: 'MOP', MR: 'MRU', MT: 'EUR', MU: 'MUR', MV: 'MVR', MW: 'MWK', MX: 'MXN',
  MY: 'MYR', MZ: 'MZN', NA: 'NAD', NE: 'XOF', NG: 'NGN', NI: 'NIO', NL: 'EUR', NO: 'NOK',
  NP: 'NPR', NR: 'AUD', NZ: 'NZD', OM: 'OMR', PA: 'PAB', PE: 'PEN', PG: 'PGK', PH: 'PHP',
  PK: 'PKR', PL: 'PLN', PT: 'EUR', PW: 'USD', PY: 'PYG', QA: 'QAR', RO: 'RON', RS: 'RSD',
  RU: 'RUB', RW: 'RWF', SA: 'SAR', SB: 'SBD', SC: 'SCR', SD: 'SDG', SE: 'SEK', SG: 'SGD',
  SI: 'EUR', SK: 'EUR', SL: 'SLL', SM: 'EUR', SN: 'XOF', SO: 'SOS', SR: 'SRD', SS: 'SSP',
  ST: 'STN', SV: 'USD', SY: 'SYP', SZ: 'SZL', TD: 'XAF', TG: 'XOF', TH: 'THB', TJ: 'TJS',
  TL: 'USD', TM: 'TMT', TN: 'TND', TO: 'TOP', TR: 'TRY', TT: 'TTD', TV: 'AUD', TZ: 'TZS',
  UA: 'UAH', UG: 'UGX', US: 'USD', UY: 'UYU', UZ: 'UZS', VA: 'EUR', VC: 'XCD', VE: 'VES',
  VN: 'VND', VU: 'VUV', WS: 'WST', YE: 'YER', ZA: 'ZAR', ZM: 'ZMW', ZW: 'ZWL',
};

/** Resolve the ISO 4217 currency code for an ISO 3166-1 alpha-2 country code. */
function countryToCurrency(cc: string): string {
  return COUNTRY_CURRENCY[cc.toUpperCase()] ?? cc;
}

export function genCurrency(location: RoamerLocation, now: number, providerId: string): FusedCurrency {
  const rng = seededRng(`${location.locationId}:currency:${localDayIndex(location, now)}:${providerId}`);
  const changePct = Math.round(between(rng, -1.4, 1.4) * 100) / 100;

  const baseCurrency = 'USD';
  // Resolve the real ISO 4217 code from the detected country (e.g. US → USD,
  // TH → THB, MX → MXN). Falls back to 'USD' when country is unknown.
  const quoteCurrency = location.countryCode
    ? countryToCurrency(location.countryCode)
    : 'USD';

  // Same-currency pair (e.g. US-based roamer): rate is exactly 1.00, no drift.
  const sameCurrency = baseCurrency === quoteCurrency;

  return {
    id: `${providerId}:currency`,
    baseCurrency,
    quoteCurrency,
    rate: sameCurrency ? 1.00 : Math.round(between(rng, 0.8, 40) * 100) / 100,
    trend: sameCurrency ? 'flat' : (changePct > 0.25 ? 'improved' : changePct < -0.25 ? 'worsened' : 'flat'),
    changePct: sameCurrency ? 0 : changePct,
    meta: EMPTY_META,
  };
}

export function genSafety(location: RoamerLocation, providerId: string): FusedSafety {
  const rng = seededRng(`${location.locationId}:safety:${providerId}`);
  const level = (chance(rng, 0.8) ? 1 : 2) as 1 | 2;
  return {
    id: `${providerId}:safety`,
    location: { latitude: location.latitude, longitude: location.longitude },
    overallScore: intBetween(rng, 55, 95),
    advisoryLevel: level,
    summary: level === 1 ? 'Exercise normal precautions.' : 'Exercise increased caution.',
    meta: EMPTY_META,
  };
}

export function genEvents(location: RoamerLocation, now: number, limit: number, providerId: string): FusedEvent[] {
  const cats = ['Music', 'Food', 'Tech', 'Art', 'Market', 'Networking', 'Wellness'];
  const out: FusedEvent[] = [];
  for (let i = 0; i < limit; i++) {
    const rng = seededRng(`${location.locationId}:events:${localDayIndex(location, now)}:${i}:${providerId}`);
    out.push({
      id: `${providerId}:events:${i}`,
      title: `${pick(rng, cats)} ${pick(rng, ['Night', 'Meetup', 'Fair', 'Session', 'Pop-up'])}`,
      category: pick(rng, cats),
      startsAt: now + intBetween(rng, 1, 14) * 86_400_000,
      venue: placeName(rng, 'attraction'),
      location: nearbyPoint(rng, location),
      priceUsd: chance(rng, 0.4) ? 0 : intBetween(rng, 5, 60),
      meta: EMPTY_META,
    });
  }
  return out;
}

export function genCommunity(location: RoamerLocation, providerId: string, placeId = location.locationId): FusedCommunity {
  const rng = seededRng(`${location.locationId}:community:${placeId}:${providerId}`);
  const avg = Math.round(between(rng, 3.4, 4.9) * 10) / 10;
  return {
    id: `${providerId}:community:${placeId}`,
    placeId,
    reviewCount: intBetween(rng, 20, 1200),
    averageRating: avg,
    sentiment: avg >= 4.3 ? 'positive' : avg >= 3.7 ? 'mixed' : 'negative',
    highlights: ['Good WiFi', 'Friendly community', 'Fair prices'].slice(0, intBetween(rng, 1, 3)),
    meta: EMPTY_META,
  };
}

export function genTransportation(location: RoamerLocation, limit: number, providerId: string): FusedTransportation[] {
  const modes: FusedTransportation['mode'][] = ['metro', 'bus', 'tram', 'rail', 'ferry', 'rideshare', 'taxi', 'bike'];
  const out: FusedTransportation[] = [];
  for (let i = 0; i < limit; i++) {
    const rng = seededRng(`${location.locationId}:transport:${i}:${providerId}`);
    const mode = pick(rng, modes);
    out.push({
      id: `${providerId}:transport:${i}`,
      mode,
      name: `${pick(rng, PLACE_PREFIX)} ${mode} line`,
      avgCostUsd: Math.round(between(rng, 0.5, 12) * 100) / 100,
      frequencyMinutes: intBetween(rng, 5, 40),
      coverageScore: intBetween(rng, 40, 95),
      meta: EMPTY_META,
    });
  }
  return out;
}

export function genMetric(location: RoamerLocation, domain: FusionDomain, providerId: string): FusedMetric {
  const rng = seededRng(`${location.locationId}:metric:${domain}:${providerId}`);
  const config: Record<string, { unit: string; label: string; min: number; max: number }> = {
    internetSpeed: { unit: 'Mbps', label: 'Avg. Internet Speed', min: 20, max: 400 },
    airQuality: { unit: 'AQI', label: 'Air Quality Index', min: 15, max: 160 },
    costOfLiving: { unit: 'index', label: 'Cost of Living Index', min: 30, max: 120 },
    visa: { unit: 'days', label: 'Visa-free Stay', min: 30, max: 180 },
  };
  const c = config[domain] ?? { unit: 'index', label: 'Metric', min: 0, max: 100 };
  return {
    id: `${providerId}:metric:${domain}`,
    location: { latitude: location.latitude, longitude: location.longitude },
    metric: domain,
    value: intBetween(rng, c.min, c.max),
    unit: c.unit,
    label: c.label,
    meta: EMPTY_META,
  };
}
